import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const PRESENTATION_STEPS = [
  {
    step: "1",
    title: "Research before you arrive",
    description:
      "Know the property's history (previous sales, listing history), comparable sales in the street and area, current competition, and the vendor's likely motivation. Walk the neighbourhood if you can. Vendors are impressed by agents who know the area, not just the numbers.",
  },
  {
    step: "2",
    title: "Build rapport first",
    description:
      "Spend the first 10-15 minutes on a genuine property tour with the vendor. Ask about the home, compliment specific features, and listen to why they are moving. The vendor needs to like and trust you before they will listen to your valuation and marketing plan.",
  },
  {
    step: "3",
    title: "Present your CMA",
    description:
      "Show 3-5 comparable sales (sold prices, not asking prices) with photos. Include properties currently on the market as context. Explain how you arrived at your valuation. Be honest about the market. Vendors respect agents who give realistic prices, even if they are not the highest.",
  },
  {
    step: "4",
    title: "Showcase your marketing",
    description:
      "This is where you differentiate yourself. Show examples of your listings: professional photography, video tours, social media campaigns, floorplans, and brochure quality. A 60-second branded video of a recent listing is more persuasive than any slide deck.",
  },
  {
    step: "5",
    title: "Explain your fee and its value",
    description:
      "Connect your fee to the marketing you provide and the results you achieve. Show that a marginally higher fee with premium marketing (video, social media, professional photography) achieves a higher net sale price than a bargain-basement agent who takes phone photos.",
  },
  {
    step: "6",
    title: "Close with a clear next step",
    description:
      "Do not leave without a commitment or a defined follow-up. 'When would you like me to start preparing the marketing materials?' is better than 'Let me know what you decide'. If they need time, arrange a specific follow-up call.",
  },
]

const DIFFERENTIATORS = [
  {
    title: "Video marketing",
    description:
      "Show a 60-second branded property video on your phone or tablet. Most agents still rely on photos alone. A polished video instantly positions you as the agent who will market their home properly.",
  },
  {
    title: "Social media reach",
    description:
      "Show your Instagram, TikTok, or Facebook page with property Reels and engagement numbers. Vendors want to know their home will be seen, not just listed on a portal and forgotten.",
  },
  {
    title: "Local market knowledge",
    description:
      "Reference specific recent sales on the street, local developments, school catchments, and neighbourhood trends. This cannot be faked and it is what separates a local expert from a postcode tourist.",
  },
  {
    title: "Buyer database",
    description:
      "Explain how you will match the property with registered buyers. If you have buyers waiting for this type of property in this area, say so. Specificity is persuasive.",
  },
  {
    title: "Results and reviews",
    description:
      "Bring 3-5 recent testimonials from happy vendors. Show your Google review rating. If you can share achieved-price-to-asking-price ratios, even better. Social proof closes deals.",
  },
  {
    title: "Communication commitment",
    description:
      "Tell the vendor exactly how often you will update them (weekly calls, fortnightly reviews), what your response time is, and who their main point of contact will be. Vendors' biggest complaint about agents is poor communication.",
  },
]

const STATS = [
  { stat: "72%", label: "of vendors instruct the agent with the best marketing" },
  { stat: "3", label: "valuations the average vendor gets before deciding" },
  { stat: "15min", label: "vendors form their impression in the first 15 minutes" },
  { stat: "2x", label: "more instructions for agents who show video in appraisals" },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "How should I prepare for a market appraisal?",
    a: "Research the property (previous sales, listing history), study comparable sales and current competition, walk the neighbourhood, and prepare a concise CMA document. Bring examples of your best marketing (photos, video, brochures). First impressions matter: dress professionally, arrive on time, and bring business cards.",
  },
  {
    q: "What is the best way to handle fee objections?",
    a: "Reframe the conversation from cost to value. Show that your marketing investment (video, professional photography, social media campaigns) achieves a higher sale price. A 1.5% fee that achieves 360,000 pounds nets the vendor more than a 0.75% fee that achieves 340,000 pounds. Use specific examples from your recent sales to illustrate this.",
  },
  {
    q: "Should I show video during the appraisal?",
    a: "Absolutely. Showing a branded 60-second property video on your phone or tablet is one of the most effective differentiators at an appraisal. Most agents still rely on photos. A polished video makes an immediate impression and shows the vendor exactly how you will market their home.",
  },
  {
    q: "How do I handle a vendor who wants a higher price?",
    a: "Present your comparable evidence clearly and honestly. Explain the risks of overpricing: fewer viewings, longer time on market, eventual price reduction, and the 'stale listing' effect. Offer to review the price after 2-3 weeks if there are no viewings. Never agree to an unrealistic price just to win the instruction.",
  },
  {
    q: "What makes vendors choose one agent over another?",
    a: "The three biggest factors are marketing quality (how they will present the home), local knowledge (how well they know the area), and communication (how responsive and proactive they are). Fee is important but rarely the deciding factor. Vendors choose the agent they trust to sell their home for the best price, not the cheapest agent.",
  },
  {
    q: "How should I follow up after an appraisal?",
    a: "Send a brief email within 2 hours thanking them for their time and summarising your valuation and marketing approach. Include a link to an example property video. Follow up with a phone call 2-3 days later. If they have not decided, offer to answer any questions. Be persistent but not pushy. Most vendors instruct within 7-10 days of their last valuation.",
  },
  {
    q: "Should I leave printed materials at the appraisal?",
    a: "Yes. Leave a concise one-page summary: your valuation, your fee, what is included in your marketing package, and 2-3 recent testimonials. This gives the vendor something to refer to when comparing agents. Do not leave a generic corporate brochure. Personalise it for their property.",
  },
  {
    q: "How do I win instructions against online agents?",
    a: "Emphasise the value of local expertise, accompanied viewings, offer negotiation, and sale progression. Online agents often leave vendors to manage viewings and communication themselves. Show your marketing quality (video, social media, professional photography) and explain how your proactive approach achieves higher sale prices, more than offsetting the fee difference.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <div className="divide-y divide-stone">
      {items.map((item) => (
        <details key={item.q} className="group py-5">
          <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
            {item.q}
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
            >
              <path
                fillRule="evenodd"
                d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </summary>
          <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
        </details>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function MarketAppraisalPresentationGuide({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Market Appraisal Presentation: How to Win Instructions",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "Market Appraisal Presentation" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Market appraisal presentation
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            How to deliver market appraisals that win instructions. A six-step
            framework, differentiators that work, and how to handle fee
            objections.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Introduction */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            The appraisal is the pitch
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              The market appraisal is the most important 45 minutes in your
              business. It is where instructions are won or lost. Yet most
              agents treat it as a price discussion when it is really a
              marketing presentation.
            </p>
            <p>
              Vendors get 2-3 valuations before deciding. They all hear similar
              prices. The agent who wins is the one who shows the best marketing,
              demonstrates the deepest local knowledge, and makes the vendor
              feel confident that their home will be presented properly.
            </p>
            <p>
              This guide breaks down the appraisal into six steps and shows you
              how to differentiate yourself from every other agent who walks
              through the door.
            </p>
          </div>
        </div>
      </section>

      {/* Six steps */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Framework
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              The six-step appraisal
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 space-y-4">
            {PRESENTATION_STEPS.map((step) => (
              <ScrollReveal key={step.step}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <div className="flex items-start gap-4">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-red font-display text-lg font-bold text-white">
                      {step.step}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-display text-lg font-bold text-charcoal">
                        {step.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Stand out
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Six ways to differentiate yourself
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {DIFFERENTIATORS.map((item, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-base font-bold text-charcoal">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {item.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {STATS.map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              { href: "/guides/winning-instructions", title: "Winning instructions", desc: "A broader guide to winning more vendor instructions." },
              { href: "/guides/estate-agent-fees", title: "Estate agent fees", desc: "Understanding and justifying your fee at appraisals." },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Read guide
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 transition-transform group-hover:translate-x-0.5">
                    <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Show, don't tell
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Walk into your next appraisal with a branded property video on your
            phone. Show vendors exactly how you will market their home.
            QuiteRight creates scroll-stopping video ads in 60 seconds.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
